import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PendingComponentBadge } from "@/components/PendingComponentBadge";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Check, X, AlertOctagon, ArrowRight, Loader2, ShieldCheck, Plus, HelpCircle } from "lucide-react";

// PRESENCE, AT THE MOMENT THE DATA GETS ENTERED (owner ruling 2026-09-25).
//
// He reviews every truck himself with the AI's read of the sheet and the photos in front of him.
// That is when he can tell whether a thing is STILL THERE — an empty engine bay shows in a
// photograph. Whether it RUNS does not. So this block sets PRESENCE and nothing else, and every
// component it touches lands at PRESENT · NOT VERIFIED.
//
// Four things it is built around, in his words:
//   · STARTS UNTICKED (his ruling, 30 Oct, D97 "never assume"): a row nobody has answered starts with NO tick —
//     one tap is ON THE TRUCK, a second GONE, a third CAN'T TELL. A row left unanswered is not written and stays
//     NOT CHECKED YET. (Before, everything the record claimed started ticked — 25 Sep, now overruled.)
//   · "CAN'T TELL IS ITS OWN STATE" — nine obvious and one engine bay no photo shows must not force
//     a guess or block the truck. "I looked and couldn't tell" is a real finding, and it says the
//     PHOTOS need redoing, which is actionable.
//   · "THE CONFIRM MUST SAY WHAT I'M ASSERTING" — the button is a sentence he is signing, not Save,
//     and it accounts for all three answers.
//   · "A TRUCK I DON'T PRESS CONFIRM ON MUST LOOK DIFFERENT" — no press, no write, and the block
//     says so in red instead of leaving a blank that reads like a clean truck.
const claimText = (c) => {
  const vals = (c.fields || []).filter((f) => f.value != null && f.value !== "")
    .map((f) => String(f.value));
  if (vals.length) return vals.slice(0, 2).join(" · ");
  if ((c.numbers || []).length) return `${c.numbers.length} number(s) recorded`;
  if ((c.extras || []).length) return (c.extras || []).slice(0, 2).map((e) => `${e.label}: ${e.value}`).join(" · ");
  return "on the record";
};
const NEXT = { open: "present", present: "gone", gone: "cant_tell", cant_tell: "present", bad: "present" };

export function PresenceReviewBlock({ donorId, data, onSaved, onGoToSpecs }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [redo, setRedo] = useState(false);
  const [picking, setPicking] = useState(null);     // component code whose damage panel is open
  const [otherText, setOtherText] = useState("");
  const [added, setAdded] = useState([]);           // codes he says it HAS though the record didn't
  const [done, setDone] = useState(null);           // {stamp, next, remaining} after the press
  const comps = data?.components || [];
  const reasons = data?.condition_reasons || [];
  const stamp = done?.stamp || data?.presence_review;

  // A NAMED, HANDS-ON GOOD IS NOT DEMOTED BY A PHOTO PASS. Somebody stood at the truck and checked
  // it; a tick on a desk review must not quietly turn that into "not verified".
  const locked = comps.filter((c) => c.condition?.state === "present_good");
  const inPlay = useMemo(() => comps.filter((c) =>
    c.condition?.state !== "present_good" &&
    (c.has_data || (c.condition && c.condition.state !== "not_assessed") || added.includes(c.code))
  ), [comps, added]);
  const unclaimed = comps.filter((c) => !c.has_data && !added.includes(c.code) &&
    (!c.condition || c.condition.state === "not_assessed"));

  const [marks, setMarks] = useState({});
  const markOf = (c) => marks[c.code] || (
    c.condition?.state === "gone" ? { kind: "gone" }
      : c.condition?.state === "cant_tell" ? { kind: "cant_tell" }
        : c.condition?.state === "present_bad"
          ? { kind: "bad", reason: c.condition.reason, note: c.condition.note }
          : c.condition?.state === "present_unverified" || added.includes(c.code) ? { kind: "present" }
            : { kind: "open" });
  const set = (code, m) => setMarks((s) => ({ ...s, [code]: m }));

  const counts = inPlay.reduce((a, c) => {
    const k = markOf(c).kind; a[k] = (a[k] || 0) + 1; return a;
  }, {});
  const nPresent = counts.present || 0, nGone = counts.gone || 0,
    nBad = counts.bad || 0, nCant = counts.cant_tell || 0, nOpen = counts.open || 0;
  const answered = inPlay.length - nOpen;

  // THE BUTTON IS THE ASSERTION. It names every answer it is about to write — an unknown row
  // hidden behind "6 on the truck, 2 gone" would overstate what he is signing.
  const confirmWords = () => {
    const left = nOpen ? ` — ${nOpen} left unanswered` : "";
    return answered ? sentence(answered) + left : "Tap the rows you can answer first";
  };
  const sentence = (n) => {
    if (!inPlay.length) return "Nothing to confirm";
    if (nGone === n) return `Confirm: none of these ${n} are on the truck`;
    if (nCant === n) return `Confirm: couldn't tell on any of these ${n} — no photo shows them`;
    if (!nGone && !nBad && !nCant) {
      return nPresent === 1 ? "Confirm this 1 component is on the truck"
                            : `Confirm these ${nPresent} components are on the truck`;
    }
    const bits = [];
    if (nPresent) bits.push(`${nPresent} on the truck`);
    if (nGone) bits.push(`${nGone} gone`);
    if (nBad) bits.push(`${nBad} damaged`);
    if (nCant) bits.push(`${nCant} couldn't tell`);
    return `Confirm: ${bits.join(", ")}`;
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { present: [], gone: [], cant_tell: [], bad: [], added };
      for (const c of inPlay) {
        const m = markOf(c);
        if (m.kind === "open") continue;          // unanswered is not written: it stays NOT CHECKED YET
        if (m.kind === "gone") body.gone.push(c.code);
        else if (m.kind === "cant_tell") body.cant_tell.push(c.code);
        else if (m.kind === "bad") body.bad.push({ component: c.code, reason: m.reason, note: m.note || null });
        else body.present.push(c.code);
      }
      const { data: r } = await api.post(`/donors/${donorId}/condition/presence-review`, body);
      setDone({ stamp: r.presence_review, next: r.next, remaining: r.remaining });
      setRedo(false);
      toast.success(`Presence recorded on this truck — ${r.remaining} still to check`);
      onSaved?.(r);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data?.can_assess) return null;

  // ALREADY CHECKED — say who and when, and let him go again without pretending it's fresh.
  if (stamp && !redo) {
    const addedCodes = (stamp.components || []).filter((code) =>
      comps.some((c) => c.code === code && !c.has_data && c.condition?.added_in_review));
    return (
      <div data-testid="presence-block-done" className="rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-bold text-emerald-900">Reviewed — somebody has said what's on it</span>
          <span data-testid="presence-stamp" className="text-[11px] font-mono text-emerald-800">
            {stamp.by} · {String(stamp.at).slice(0, 10)} · {stamp.present} on the truck
            {stamp.gone ? ` · ${stamp.gone} gone` : ""}{stamp.bad ? ` · ${stamp.bad} damaged` : ""}
            {stamp.cant_tell ? ` · ${stamp.cant_tell} couldn't tell` : ""}
          </span>
        </div>
        <p className="text-[11px] text-emerald-800/80 mt-1">
          Presence only. Nothing here says anything runs — that stays <b>NOT VERIFIED</b> until
          somebody who had hands on it is named against it.
        </p>
        {/* "I know it's got one, I can't describe it" — the gap gets named, and the spec fields are
            one tap away rather than a gate he had to pass to record what he saw. */}
        {addedCodes.length > 0 && (
          <p data-testid="presence-added-needs-specs" className="text-[11px] text-[#C76714] mt-1.5">
            You added {addedCodes.length} component{addedCodes.length === 1 ? "" : "s"} the record
            never claimed, with nothing recorded about {addedCodes.length === 1 ? "it" : "them"}.
            {onGoToSpecs && (
              <button data-testid="presence-goto-specs" onClick={() => onGoToSpecs(addedCodes[0])}
                className="underline font-bold ml-1">add the numbers now</button>
            )}
            {" "}— or leave it: it's on the <b>Confirmed present, nothing recorded</b> list in Health
            &amp; Integrity until somebody standing at the truck can read the serial.
          </p>
        )}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <button data-testid="presence-recheck" onClick={() => setRedo(true)}
            className="text-[11px] underline text-emerald-900">Review it again</button>
          {done?.next && (
            <button data-testid="presence-next-truck"
              onClick={() => navigate(`/donors/${done.next.donor_id}?review=presence`)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#B01E28] hover:bg-[#8E1820] text-white text-xs font-bold px-3 h-9">
              Next truck to review ({done.remaining} left) — {done.next.stock_number}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="presence-block" className="rounded-xl border-2 border-[#E0781A] bg-orange-50/40 p-3 space-y-2.5">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-slate-900">What's still on it?</span>
          <span data-testid="presence-scope" className="text-[11px] text-slate-500">
            {inPlay.length} component{inPlay.length === 1 ? "" : "s"} the record claims
          </span>
        </div>
        {!stamp && (
          <p data-testid="presence-never-checked" className="text-[11px] text-[#B01E28] font-semibold mt-0.5">
            This truck has never been reviewed — the record lists these and nobody has
            said whether they're still on it.
          </p>
        )}
        <p data-testid="presence-tap-hint" className="text-[10px] text-slate-500 mt-0.5">
          Nothing starts ticked. Tap a row once for <b>ON THE TRUCK</b>, twice for <b>GONE</b>, three
          times for <b>CAN'T TELL</b> (you looked, no photo shows it). A row you don't answer stays
          <b> NOT CHECKED YET</b>.
        </p>
      </div>

      <div className="space-y-1">
        {inPlay.map((c) => {
          const m = markOf(c);
          const gone = m.kind === "gone", bad = m.kind === "bad", cant = m.kind === "cant_tell", open = m.kind === "open";
          return (
            <div key={c.code} data-testid={`presence-row-${c.code}`} data-answer={m.kind}
              className={`rounded-lg border-2 transition-colors ${gone ? "border-slate-700 bg-slate-800" : cant ? "border-[#E0781A] bg-orange-100/70" : bad ? "border-[#B01E28] bg-red-50" : open ? "border-slate-300 bg-white" : "border-emerald-200 bg-white"}`}>
              <div className="flex items-stretch">
                {/* THE UNTICK IS THE WORK — the whole left block is the target, and the row cycles
                    present → gone → can't tell, because most taps are "gone". */}
                <button type="button" data-testid={`presence-toggle-${c.code}`}
                  onClick={() => set(c.code, { kind: NEXT[m.kind] || "gone" })}
                  className="flex-1 min-w-0 flex items-center gap-2.5 text-left px-2.5 py-2">
                  <span className={`shrink-0 w-7 h-7 rounded-md border-2 grid place-items-center ${gone ? "border-white bg-slate-700" : cant ? "border-[#C76714] bg-white" : bad ? "border-[#B01E28] bg-white" : open ? "border-slate-400 bg-white" : "border-emerald-500 bg-emerald-500"}`}>
                    {gone ? <X className="w-4 h-4 text-white" />
                      : cant ? <HelpCircle className="w-4 h-4 text-[#C76714]" />
                        : bad ? <AlertOctagon className="w-4 h-4 text-[#B01E28]" />
                          : open ? null : <Check className="w-4 h-4 text-white" />}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm font-bold ${gone ? "text-white" : "text-slate-800"}`}>
                      {c.label}
                      <span className="ml-1.5"><PendingComponentBadge show={c.pending_group} testid={`presence-pending-${c.code}`} /></span>
                      {added.includes(c.code) && <span className="ml-1.5 text-[10px] font-normal text-[#C76714]">you added this — the record never claimed it</span>}
                    </span>
                    <span className={`block text-[11px] truncate ${gone ? "text-slate-300 line-through" : cant ? "text-[#C76714]" : "text-slate-500"}`}>
                      {gone ? "GONE — the spec stays as a record that it had one"
                        : cant ? "CAN'T TELL — you looked and no photo shows it (the photos need redoing)"
                          : bad ? `KNOWN BAD · ${(reasons.find((r) => r.code === m.reason) || {}).label || m.reason}${m.note ? ` — ${m.note}` : ""}`
                            : open ? `NOT CHECKED YET · the record says: ${claimText(c)}` : claimText(c)}
                    </span>
                  </span>
                </button>
                {!gone && !cant && (
                  <button type="button" data-testid={`presence-damage-${c.code}`}
                    onClick={() => { setPicking(picking === c.code ? null : c.code); setOtherText(m.note || ""); }}
                    className={`shrink-0 px-2.5 text-[10px] font-bold uppercase tracking-wide border-l-2 ${bad ? "border-[#B01E28] text-[#B01E28]" : "border-slate-100 text-slate-400 hover:text-[#B01E28]"}`}>
                    damage
                  </button>
                )}
              </div>
              {picking === c.code && (
                <div data-testid={`presence-reasons-${c.code}`} className="border-t-2 border-[#B01E28]/20 p-2 space-y-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#B01E28]">
                    Only what you can actually SEE — not whether it runs
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {reasons.map((r) => (
                      <button key={r.code} type="button" data-testid={`presence-reason-${c.code}-${r.code}`}
                        onClick={() => { set(c.code, { kind: "bad", reason: r.code }); setPicking(null); }}
                        className={`h-8 px-2.5 rounded-lg border-2 text-[11px] font-bold ${m.reason === r.code ? "border-[#B01E28] bg-[#B01E28] text-white" : "border-[#B01E28]/40 bg-white text-[#B01E28] hover:bg-red-100"}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Input data-testid={`presence-other-${c.code}`} value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      placeholder="Other — say it in a word or two" className="h-8 text-xs rounded-lg" />
                    <button type="button" data-testid={`presence-other-save-${c.code}`} disabled={!otherText.trim()}
                      onClick={() => { set(c.code, { kind: "bad", reason: "other", note: otherText.trim() }); setPicking(null); }}
                      className="h-8 px-3 rounded-lg bg-[#B01E28] text-white text-xs font-bold disabled:opacity-40">Use</button>
                    <button type="button" onClick={() => setPicking(null)} className="text-[11px] text-slate-500 underline px-1">cancel</button>
                  </div>
                </div>
              )}
              {/* A component he noticed himself has nothing recorded about it — the specs are one
                  tap away, never a gate. */}
              {added.includes(c.code) && onGoToSpecs && (
                <div className="px-2.5 pb-2">
                  <button type="button" data-testid={`presence-added-specs-${c.code}`}
                    onClick={() => onGoToSpecs(c.code)}
                    className="text-[10px] text-[#C76714] underline">
                    got the serial in front of you? add its numbers
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {inPlay.length === 0 && (
          <p data-testid="presence-nothing-claimed" className="text-[11px] text-slate-500">
            The record doesn't claim a single component on this truck yet — there's nothing to
            confirm the presence of. Record what it has first, or tap one below if you can see it.
          </p>
        )}
      </div>

      {/* WHY "GOOD" ISN'T HERE — an omitted option looks like a missing feature. */}
      <p data-testid="presence-what-desk-can-set" className="text-[11px] text-slate-600 border-l-2 border-slate-300 pl-2">
        From here you can say it's <b>on the truck</b>, it's <b>gone</b>, that you can <b>see
        damage</b>, or that you <b>can't tell</b>. <b>GOOD</b> is not a tick on this pass: it needs
        the name of somebody who had hands on it. You can record one on any component — here or on
        the tablet — by naming who checked it.
      </p>

      {locked.length > 0 && (
        <p data-testid="presence-locked-good" className="text-[11px] text-emerald-800">
          {locked.map((c) => `${c.label}${c.condition?.provenance?.line ? ` (${c.condition.provenance.line})` : ""}`).join(" · ")}
          {" "}— already <b>GOOD</b>, with a name on it. Left alone by this pass; use the condition
          buttons on the component to change one.
        </p>
      )}

      {unclaimed.length > 0 && (
        <div data-testid="presence-unclaimed" className="text-[11px] text-slate-500">
          <b>Not assessed — nothing recorded for these.</b> The record never said this truck had one,
          which is not the same as nobody having looked. Tap one if you can see it has it:
          <div className="flex flex-wrap gap-1 mt-1">
            {unclaimed.map((c) => (
              <button key={c.code} type="button" data-testid={`presence-add-${c.code}`}
                onClick={() => setAdded((a) => [...a, c.code])}
                className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 hover:border-[#E0781A] hover:text-[#C76714]">
                <Plus className="w-3 h-3" />it's got {c.label.toLowerCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-1">
        <button type="button" data-testid="presence-confirm" disabled={busy || answered === 0}
          onClick={save}
          className="w-full h-12 rounded-lg bg-[#B01E28] hover:bg-[#8E1820] disabled:opacity-40 text-white font-bold tracking-tight">
          {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : confirmWords()}
        </button>
        <p className="text-[10px] text-slate-500 mt-1 text-center">
          Your name goes on every row. Nothing here says anything runs — what you tick lands
          <b> PRESENT · NOT VERIFIED</b>. Leave it unpressed and this truck stays <b>NOT
          REVIEWED</b>, which is how a truck you skipped stays visibly different from one you did.
        </p>
      </div>
    </div>
  );
}
